/**
 * World — Central ECS manager.
 *
 * Manages entities, component storage, system scheduling, queries, and lifecycle.
 * Entity operations (create/destroy) are deferred during system execution and
 * flushed between sub-phases.
 */

import * as THREE from 'three/webgpu';
import { MAX_ENTITY_COUNT } from '../shared/constants.js';
import type { LoopPhase, SubPhase } from '../shared/types.js';
import type { ComponentDef, ComponentSchema, TagDef } from './Component.js';
import {
  EntityBuilder,
  EntityState,
  type EntityLifecycle,
  type EntityRecord,
} from './Entity.js';
import { QueryResult } from './Query.js';
import { SystemScheduler, type ISystem, type SystemScheduleEntry } from './System.js';
import type { EventBus } from '../core/EventBus.js';
import { Logger } from '../core/Logger.js';

// ─── Types ──────────────────────────────────────────────────────────────────────

interface DeferredOp {
  type: 'destroy';
  entity: number;
}

// SubPhase is a const enum (isolatedModules), so values are inlined.
// Hoisted here to avoid per-frame allocation in runPhase().
// 0 = PreUpdate, 1 = Update, 2 = PostUpdate
const SUB_PHASES: SubPhase[] = [0, 1, 2] as SubPhase[];

// ─── World ──────────────────────────────────────────────────────────────────────

export class World {
  private entities = new Map<number, EntityRecord>();
  // Dense id-indexed mirrors of the per-entity storage. Entity ids are small
  // sequential integers (recycled via freeIds), so an array slot is an O(1)
  // ~1 ns load where a Map.get(int) is ~15-20 ns — and query evaluation,
  // isAlive/hasComponent/getComponent all sit on that lookup per entity per
  // system per frame. `entities` (the Map) is kept as the source of
  // iteration ORDER (getAliveEntities/singleton/dispose); `_recordById`
  // mirrors it at its two mutation points. Component/tag storage lives ONLY
  // in the arrays (nothing iterated them). Slots grow with nextEntityId so
  // the arrays stay packed (never holey/dictionary mode).
  private _recordById: (EntityRecord | undefined)[] = [];
  private _compsById: (Map<number, Record<string, unknown>> | undefined)[] = [];
  private _tagsById: (Set<number> | undefined)[] = [];
  // Reverse membership indexes (componentId/tagId → entity set). Queries
  // iterate the SMALLEST required set instead of scanning every alive entity
  // — without these, every query() evaluation was O(world) regardless of how
  // few entities matched. Kept exact at the same four mutation points that
  // maintain the forward maps, plus entity destruction.
  private componentIndex = new Map<number, Set<number>>();
  private tagIndex = new Map<number, Set<number>>();
  private nextEntityId = 1;
  private freeIds: number[] = [];
  private deferredOps: DeferredOp[] = [];
  private _pendingDestroys = new Set<number>();
  /** Scratch for _destroyEntityImmediate's child-entity promotion pass. */
  private _promoteScratch: THREE.Object3D[] = [];
  private _executingDepth = 0;
  private _disposed = false;

  readonly scheduler = new SystemScheduler();
  readonly scene = new THREE.Scene();

  constructor(private events: EventBus) {}

  // ─── Entity Management ───────────────────────────────────────────────────

  createEntity(name?: string): EntityBuilder {
    return new EntityBuilder(this, name ?? `Entity_${this.nextEntityId}`);
  }

  /**
   * Internal: Called by EntityBuilder.build(). Do not call directly.
   */
  _buildEntity(
    name: string,
    components: Array<{ def: ComponentDef; data?: Record<string, unknown> }>,
    tags: TagDef[],
    lifecycle: EntityLifecycle,
    parentId: number | null,
  ): number {
    if (this._disposed) {
      throw new Error('Cannot create entity on a disposed World');
    }
    if (this.entities.size >= MAX_ENTITY_COUNT) {
      throw new Error(`Maximum entity count (${MAX_ENTITY_COUNT}) exceeded`);
    }

    const id = this.freeIds.length > 0 ? this.freeIds.pop()! : this.nextEntityId++;
    const object3D = new THREE.Object3D();
    object3D.name = name;
    object3D.userData.entityId = id;

    const record: EntityRecord = {
      id,
      name,
      state: EntityState.Created,
      object3D,
      lifecycle,
      parent: parentId,
    };

    this.entities.set(id, record);
    this._ensureSlots(id);
    this._recordById[id] = record;
    this._tagsById[id] = new Set();

    // Add to scene graph.
    // Check state !== Destroyed — during onDestroy callbacks the dying entity
    // is still in the map but about to be removed from the scene graph.
    // Attaching to it would silently orphan the child.
    if (parentId !== null) {
      const parentRecord = this._recordById[parentId];
      if (parentRecord && parentRecord.state !== EntityState.Destroyed) {
        parentRecord.object3D.add(object3D);
      } else {
        this.scene.add(object3D);
      }
    } else {
      this.scene.add(object3D);
    }

    // Attach components
    for (const { def, data } of components) {
      this._setComponent(id, def, data);
    }

    // Attach tags — through addTag so the reverse tagIndex is maintained:
    // Query narrows candidates on that index and returns ZERO matches for a
    // required tag nobody is indexed under (builder-tagged entities were
    // invisible to `world.query(Transform, IsPlayer)`).
    for (const tag of tags) this.addTag(id, tag);

    // Lifecycle: Created → Initialized
    // Wrapped in try/catch so a throwing callback does not leave the
    // entity stuck in Initialized state with entity:created never fired.
    record.state = EntityState.Initialized;
    try {
      lifecycle.onInit?.(id);
    } catch (err) {
      Logger.error('World', `Entity ${id} onInit error: ${err}`);
    }

    // Guard: lifecycle callback may have destroyed this entity
    if (this._recordById[id] === undefined) return id;

    // Lifecycle: Initialized → Active
    record.state = EntityState.Active;
    try {
      lifecycle.onEnable?.(id);
    } catch (err) {
      Logger.error('World', `Entity ${id} onEnable error: ${err}`);
    }

    if (this._recordById[id] === undefined) return id;

    this.events.emit('entity:created', { entity: id });

    return id;
  }

  destroyEntity(entity: number): void {
    if (this._executingDepth > 0) {
      // Deduplicate: a second deferred destroy for the same ID could target
      // a different entity if the ID was recycled during flush.
      if (!this._pendingDestroys.has(entity)) {
        this._pendingDestroys.add(entity);
        this.deferredOps.push({ type: 'destroy', entity });
      }
      return;
    }
    this._destroyEntityImmediate(entity);
  }

  private _destroyEntityImmediate(entity: number): void {
    const record = this._recordById[entity];
    if (!record || record.state === EntityState.Destroyed) return;

    // Lifecycle: Active → Destroyed
    // Errors are isolated so cleanup always runs.
    if (record.state === EntityState.Active) {
      try {
        record.lifecycle.onDisable?.(entity);
      } catch (err) {
        Logger.error('World', `Entity ${entity} onDisable error: ${err}`);
      }
    }
    record.state = EntityState.Destroyed;
    try {
      record.lifecycle.onDestroy?.(entity);
    } catch (err) {
      Logger.error('World', `Entity ${entity} onDestroy error: ${err}`);
    }

    // Emit before cleanup so listeners can still access entity data
    // (components, tags, Object3D) via World methods.
    this.events.emit('entity:destroyed', { entity });

    // Reparent children to scene root before detaching so alive child
    // entities don't become invisible when their parent is destroyed.
    // scene.add() auto-removes from the current parent. Also clear the
    // ECS-level `parent` field on each child's EntityRecord so code that
    // walks the parent chain doesn't see a ghost ancestor referencing the
    // destroyed id (e.g., after the id is recycled it would silently
    // attribute the child to a different entity).
    const children = record.object3D.children;
    // Only ALIVE child entities are promoted. Plain meshes/lights attached to
    // the entity's Object3D were being reparented to the scene root too,
    // leaving ghost visuals behind (HtmlDisposal disposes their geometry right
    // before destroyEntity — the orphaned ghost then re-uploaded it). Non-entity
    // children now leave the scene graph with their owner. Two passes because
    // scene.add() splices the promoted child out of `children` mid-walk.
    const promote = this._promoteScratch;
    promote.length = 0;
    for (let i = 0; i < children.length; i++) {
      const childObj = children[i];
      const childId = (childObj as THREE.Object3D).userData?.entityId;
      if (typeof childId !== 'number') continue;
      const childRecord = this._recordById[childId];
      if (!childRecord || childRecord.state === EntityState.Destroyed) continue;
      if (childRecord.parent === entity) childRecord.parent = null;
      promote.push(childObj);
    }
    for (let i = 0; i < promote.length; i++) this.scene.add(promote[i]);
    promote.length = 0;
    record.object3D.removeFromParent();

    // Clean up storage (reverse indexes first — they key off the forward maps)
    const comps = this._compsById[entity];
    if (comps) {
      for (const cid of comps.keys()) this.componentIndex.get(cid)?.delete(entity);
    }
    const tags = this._tagsById[entity];
    if (tags) {
      for (const tid of tags) this.tagIndex.get(tid)?.delete(entity);
    }
    this._compsById[entity] = undefined;
    this._tagsById[entity] = undefined;
    this._recordById[entity] = undefined;
    this.entities.delete(entity);
    this.freeIds.push(entity);
  }

  /** Grow the id-indexed arrays to cover `id` without leaving holes. */
  private _ensureSlots(id: number): void {
    const arr = this._recordById;
    if (id < arr.length) return;
    const comps = this._compsById;
    const tags = this._tagsById;
    for (let i = arr.length; i <= id; i++) {
      arr.push(undefined);
      comps.push(undefined);
      tags.push(undefined);
    }
  }

  isAlive(entity: number): boolean {
    const record = this._recordById[entity];
    return record !== undefined && record.state !== EntityState.Destroyed;
  }

  getName(entity: number): string {
    return this._recordById[entity]?.name ?? '';
  }

  getObject3D(entity: number): THREE.Object3D {
    const record = this._recordById[entity];
    if (!record) throw new Error(`Entity ${entity} not found`);
    return record.object3D;
  }

  get entityCount(): number {
    return this.entities.size;
  }

  /**
   * Collect currently-alive entity IDs.
   *
   * @param out  Optional caller-owned array. When provided it is cleared
   *             and filled in place so hot-path callers (query evaluation,
   *             SpatialSystem, AOI InterestManager, …) can reuse the same
   *             buffer across frames instead of allocating a fresh `number[]`
   *             on every call. Returns the same array when `out` is given.
   */
  getAliveEntities(out?: number[]): number[] {
    const result = out ?? [];
    result.length = 0;
    for (const [id, record] of this.entities) {
      if (record.state !== EntityState.Destroyed) {
        result.push(id);
      }
    }
    return result;
  }

  // ─── Component Access ────────────────────────────────────────────────────

  addComponent<S extends ComponentSchema>(
    entity: number,
    def: ComponentDef<S>,
    data?: Partial<Record<keyof S, unknown>>,
  ): void {
    // Check alive (not just existence) — during the destroy window the entity
    // is still in the map with state Destroyed; mutations should be blocked.
    const record = this._recordById[entity];
    if (!record || record.state === EntityState.Destroyed) return;
    this._setComponent(entity, def, data as Record<string, unknown> | undefined);
  }

  private _setComponent(
    entity: number,
    def: ComponentDef,
    data?: Record<string, unknown>,
  ): void {
    let entityComponents = this._compsById[entity];
    if (!entityComponents) {
      entityComponents = new Map();
      this._ensureSlots(entity);
      this._compsById[entity] = entityComponents;
    }
    entityComponents.set(def.id, data ?? {});
    let index = this.componentIndex.get(def.id);
    if (!index) {
      index = new Set();
      this.componentIndex.set(def.id, index);
    }
    index.add(entity);
  }

  removeComponent(entity: number, def: ComponentDef): void {
    this._compsById[entity]?.delete(def.id);
    this.componentIndex.get(def.id)?.delete(entity);
  }

  hasComponent(entity: number, def: ComponentDef): boolean {
    return this._compsById[entity]?.has(def.id) ?? false;
  }

  /**
   * @internal Live membership index for a component — entities currently
   * holding it. Query evaluation iterates the smallest of these instead of
   * scanning the whole world. Callers must NOT mutate the returned set, and
   * must still filter by isAlive (destroy-window entities remain indexed
   * until the actual destroy flush).
   */
  getComponentIndex(def: ComponentDef): ReadonlySet<number> | undefined {
    return this.componentIndex.get(def.id);
  }

  /** @internal Tag counterpart of getComponentIndex. */
  getTagIndex(tag: TagDef): ReadonlySet<number> | undefined {
    return this.tagIndex.get(tag.id);
  }

  /**
   * @internal Archetype test used by QueryResult evaluation: alive AND holds
   * every required def AND holds none of the excluded defs. One lookup each
   * into the entity/component/tag maps instead of isAlive + hasComponent/
   * hasTag per def (2 map hits each) — query evaluation runs per system per
   * frame over every candidate, so this dominated FixedSim query cost.
   */
  _matchesArchetype(
    entity: number,
    required: readonly ComponentDef[],
    excluded: readonly (ComponentDef | TagDef)[],
  ): boolean {
    const record = this._recordById[entity];
    if (record === undefined || record.state === EntityState.Destroyed) return false;
    const comps = this._compsById[entity];
    const tags = this._tagsById[entity];
    for (let i = 0; i < required.length; i++) {
      const def = required[i];
      if (def.isTag) {
        if (tags === undefined || !tags.has(def.id)) return false;
      } else if (comps === undefined || !comps.has(def.id)) {
        return false;
      }
    }
    for (let i = 0; i < excluded.length; i++) {
      const def = excluded[i];
      if (def.isTag) {
        if (tags !== undefined && tags.has(def.id)) return false;
      } else if (comps !== undefined && comps.has(def.id)) {
        return false;
      }
    }
    return true;
  }

  getComponent<S extends ComponentSchema>(
    entity: number,
    def: ComponentDef<S>,
  ): Record<string, unknown> {
    const data = this._compsById[entity]?.get(def.id);
    if (data === undefined) {
      throw new Error(`Entity ${entity} does not have component "${def.name}"`);
    }
    return data;
  }

  tryGetComponent<S extends ComponentSchema>(
    entity: number,
    def: ComponentDef<S>,
  ): Record<string, unknown> | undefined {
    return this._compsById[entity]?.get(def.id);
  }

  addTag(entity: number, tag: TagDef): void {
    const tags = this._tagsById[entity];
    if (!tags) return; // mirror the old no-op for unknown entities
    tags.add(tag.id);
    let index = this.tagIndex.get(tag.id);
    if (!index) {
      index = new Set();
      this.tagIndex.set(tag.id, index);
    }
    index.add(entity);
  }

  removeTag(entity: number, tag: TagDef): void {
    this._tagsById[entity]?.delete(tag.id);
    this.tagIndex.get(tag.id)?.delete(entity);
  }

  hasTag(entity: number, tag: TagDef): boolean {
    return this._tagsById[entity]?.has(tag.id) ?? false;
  }

  // ─── Queries ─────────────────────────────────────────────────────────────

  query(...required: ComponentDef[]): QueryResult {
    return new QueryResult(this, required);
  }

  singleton<S extends ComponentSchema>(def: ComponentDef<S>): Record<string, unknown> {
    for (const [eid, record] of this.entities) {
      if (record.state === EntityState.Destroyed) continue;
      const data = this._compsById[eid]?.get(def.id);
      if (data !== undefined) return data;
    }
    throw new Error(`No singleton entity found with component "${def.name}"`);
  }

  // ─── Systems ─────────────────────────────────────────────────────────────

  addSystem(system: ISystem): void {
    this.scheduler.add(system);
  }

  removeSystem(name: string): void {
    const removed = this.scheduler.remove(name);
    if (removed?.dispose) {
      try {
        removed.dispose();
      } catch (err) {
        Logger.error('World', `System "${name}" dispose error: ${err}`);
      }
    }
  }

  getSchedule(): SystemScheduleEntry[] {
    return this.scheduler.getSchedule();
  }

  // ─── Execution ───────────────────────────────────────────────────────────

  /**
   * Run all systems for a phase. Flushes deferred ops between sub-phases.
   */
  runPhase(phase: LoopPhase, dt: number): void {
    if (this._disposed) return;
    this._executingDepth++;

    try {
      for (const subPhase of SUB_PHASES) {
        const systems = this.scheduler.getSystemsForSubPhase(
          phase,
          subPhase as SubPhase,
        );
        for (const sys of systems) {
          try {
            sys.execute(dt, this);
          } catch (err) {
            Logger.error('World', `System "${sys.name}" execute error: ${err}`);
          }
        }
        this.flush();
      }
    } finally {
      this._executingDepth--;
    }
  }

  /**
   * Flush all deferred operations.
   */
  flush(): void {
    // Loop to handle ops enqueued by onDestroy callbacks during flush
    while (this.deferredOps.length > 0) {
      const ops = this.deferredOps.splice(0);
      for (const op of ops) {
        if (op.type === 'destroy') {
          // Clear from pending set BEFORE destroy — callbacks may re-queue
          // the same ID legitimately if it was recycled to a new entity.
          this._pendingDestroys.delete(op.entity);
          this._destroyEntityImmediate(op.entity);
        }
      }
    }
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────

  initSystems(): void {
    this.scheduler.initAll(this);
  }

  dispose(): void {
    if (this._disposed) return;
    this._disposed = true;
    // Snapshot keys to avoid Map mutation during iteration
    // (_destroyEntityImmediate deletes from this.entities)
    const ids = Array.from(this.entities.keys());
    for (const id of ids) {
      this._destroyEntityImmediate(id);
    }

    this._pendingDestroys.clear();

    // Dispose all systems
    this.scheduler.disposeAll();
  }
}
